export type PlatformAdministratorSummary = {
  membershipId: string;
  userId: string;
  email: string;
  displayName: string;
  role: string;
  status: string;
  userActive: boolean;
  mfaConfigured: boolean;
  createdAt: string;
  updatedAt: string;
  version: number;
  activeSessionCount: number;
};

export type PlatformAdministratorMutationResponse = {
  administrator: PlatformAdministratorSummary;
  revokedSessionCount: number;
};

export type PlatformSessionSummary = {
  sessionId: string;
  membershipId: string;
  userId: string;
  role: string;
  status: string;
  authenticationMethod: string;
  createdAt: string;
  lastSeenAt: string;
  expiresAt: string;
  revokedAt: string | null;
  revocationReason: string | null;
};

export type PlatformSessionMutationResponse = {
  session: PlatformSessionSummary;
};

export type PlatformSessionBulkMutationResponse = {
  membershipId: string;
  revokedSessionCount: number;
};

type AdministratorListResponse = { items: PlatformAdministratorSummary[] };
type SessionListResponse = { items: PlatformSessionSummary[] };

function apiError(response: Response, payload: unknown): Error {
  const problem = payload && typeof payload === "object"
    ? payload as Record<string, unknown>
    : {};
  const correlationId = typeof problem.correlationId === "string"
    ? problem.correlationId
    : response.headers.get("X-Correlation-Id") ?? undefined;
  const error = new Error("Platform security administration request failed.");
  Object.assign(error, {
    status: response.status,
    problem: { ...problem, correlationId }
  });
  return error;
}

async function platformSecurityFetch<T>(path: string, token: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    ...options,
    headers: {
      Accept: "application/json",
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      Authorization: `Bearer ${token}`,
      ...(options.headers ?? {})
    }
  });

  if (response.status === 204) return null as T;
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw apiError(response, payload);
  return payload as T;
}

function mutationOptions(body: unknown, idempotencyKey: string): RequestInit {
  return {
    method: "POST",
    headers: { "Idempotency-Key": idempotencyKey },
    body: JSON.stringify(body)
  };
}

function createIdempotencySuffix(): string {
  if (typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  const random = new Uint32Array(4);
  crypto.getRandomValues(random);
  return Array.from(random, (value) => value.toString(16).padStart(8, "0")).join("");
}

export function createPlatformSecurityIdempotencyKey(prefix: string): string {
  return `${prefix}:${createIdempotencySuffix()}`;
}

export async function getPlatformAdministrators(token: string): Promise<PlatformAdministratorSummary[]> {
  const response = await platformSecurityFetch<AdministratorListResponse>(
    "/api/v1/platform-console/administrators",
    token
  );
  return response.items;
}

export function addPlatformAdministrator(
  token: string,
  email: string,
  reason: string,
  idempotencyKey: string
): Promise<PlatformAdministratorMutationResponse> {
  return platformSecurityFetch<PlatformAdministratorMutationResponse>(
    "/api/v1/platform-console/administrators",
    token,
    mutationOptions({ email, reason }, idempotencyKey)
  );
}

export function mutatePlatformAdministrator(
  token: string,
  membershipId: string,
  action: "suspend" | "reactivate" | "revoke",
  expectedVersion: number,
  reason: string,
  idempotencyKey: string
): Promise<PlatformAdministratorMutationResponse> {
  return platformSecurityFetch<PlatformAdministratorMutationResponse>(
    `/api/v1/platform-console/administrators/${encodeURIComponent(membershipId)}/${action}`,
    token,
    mutationOptions({ expectedVersion, reason }, idempotencyKey)
  );
}

export async function getPlatformAdministratorSessions(
  token: string,
  membershipId: string
): Promise<PlatformSessionSummary[]> {
  const response = await platformSecurityFetch<SessionListResponse>(
    `/api/v1/platform-console/administrators/${encodeURIComponent(membershipId)}/sessions?limit=100`,
    token
  );
  return response.items;
}

export function revokePlatformSession(
  token: string,
  sessionId: string,
  reason: string,
  idempotencyKey: string
): Promise<PlatformSessionMutationResponse> {
  return platformSecurityFetch<PlatformSessionMutationResponse>(
    `/api/v1/platform-console/administrators/sessions/${encodeURIComponent(sessionId)}/revoke`,
    token,
    mutationOptions({ reason }, idempotencyKey)
  );
}

export function revokeAllPlatformAdministratorSessions(
  token: string,
  membershipId: string,
  reason: string,
  idempotencyKey: string
): Promise<PlatformSessionBulkMutationResponse> {
  return platformSecurityFetch<PlatformSessionBulkMutationResponse>(
    `/api/v1/platform-console/administrators/${encodeURIComponent(membershipId)}/sessions/revoke-all`,
    token,
    mutationOptions({ reason }, idempotencyKey)
  );
}
